import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertTriangle, Clock as ClockIcon } from 'lucide-react';
import {
  updateEvent, buildGeoInfoWithApprover, buildGeoInfoWithModifier,
  EVENT_TYPES, EVENT_TYPE_LABELS, APPROVAL_STATUS, APPROVAL_STATUS_LABELS,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';
import {
  DEFAULT_TIME_ZONE, timeZoneLabel, dayKeyInZone, formatHmInZone, zonedDateTimeToInstant,
} from '../lib/workerTime';

// Convierte un ISO timestamp del backend a formato "YYYY-MM-DDTHH:mm"
// que el input datetime-local entiende, EN LA ZONA DEL TRABAJADOR (no la
// del navegador del admin). Los segundos se pierden — el input no los
// soporta de serie (los originales se conservan si no se toca la hora).
const isoToLocalInput = (iso, tz = DEFAULT_TIME_ZONE) => {
  if (!iso) return '';
  const day = dayKeyInZone(iso, tz);
  const hm = formatHmInZone(iso, tz);
  if (!day || !hm) return '';
  return `${day}T${hm}`;
};

// Inverso: del valor del input de vuelta a un ISO UTC, interpretando la
// fecha y la hora en la zona del trabajador.
const localInputToIso = (localStr, tz = DEFAULT_TIME_ZONE) => {
  if (!localStr) return null;
  // localStr = "YYYY-MM-DDTHH:MM"
  const d = zonedDateTimeToInstant(String(localStr).slice(0, 10), String(localStr).slice(11, 16), tz);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

// Tipos que el admin puede asignar al editar un fichaje. Excluimos los
// 'open' tipos paired si no aplica — por ahora mostramos todos y el
// admin decide. Los agrupamos para que el select sea más navegable.
const EVENT_TYPE_GROUPS = [
  { label: 'Jornada', items: [EVENT_TYPES.CLOCK_IN, EVENT_TYPES.CLOCK_OUT] },
  { label: 'Pausa', items: [EVENT_TYPES.PAUSE_START, EVENT_TYPES.PAUSE_END] },
  { label: 'Vacaciones', items: [EVENT_TYPES.VACATION_START, EVENT_TYPES.VACATION_END] },
  { label: 'Día completo', items: [EVENT_TYPES.SICK_LEAVE_START, EVENT_TYPES.SICK_LEAVE_END] },
  { label: 'Compensación', items: [EVENT_TYPES.COMPENSATION_START, EVENT_TYPES.COMPENSATION_END] },
  { label: 'Por horas', items: [EVENT_TYPES.ABSENCE_START, EVENT_TYPES.ABSENCE_END] },
];

// `timeZone` (opcional): zona del trabajador dueño del evento
// (timeZoneForWorker). Por defecto Madrid, nunca la del navegador.
const EventEditModal = ({ open, event, onClose, onSaved, timeZone = null }) => {
  const { user: authUser } = useAuth();
  const toast = useToast();
  const tz = timeZone || DEFAULT_TIME_ZONE;
  const tzHint = timeZoneLabel(tz);
  const [eventType, setEventType] = useState(EVENT_TYPES.CLOCK_IN);
  const [localTimestamp, setLocalTimestamp] = useState('');
  const [approvalStatus, setApprovalStatus] = useState(APPROVAL_STATUS.APPROVED);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !event) return;
    setEventType(Number(event.eventType));
    setLocalTimestamp(isoToLocalInput(event.timestamp, tz));
    setApprovalStatus(Number(event.approvalStatus ?? APPROVAL_STATUS.APPROVED));
    setError(null);
  }, [open, event, tz]);

  if (!open || !event) return null;

  const handleSubmit = async (ev) => {
    ev?.preventDefault?.();
    setError(null);
    const newIso = localInputToIso(localTimestamp, tz);
    if (!newIso) {
      setError('La fecha/hora no es válida.');
      return;
    }
    setSubmitting(true);
    try {
      // Audit trail: dos flags distintos, según qué se haya cambiado.
      //   · statusChanged → ;apprby= (es una nueva aprobación/rechazo)
      //   · sin statusChanged pero edita hora/tipo → ;modby= (edición)
      // Si SOLO cambió status, conservamos el ;modby= anterior intacto.
      // Si SOLO cambió hora/tipo, conservamos el ;apprby= anterior.
      const statusChanged = Number(event.approvalStatus ?? APPROVAL_STATUS.APPROVED) !== Number(approvalStatus);
      const contentChanged = Number(event.eventType) !== Number(eventType)
        || new Date(event.timestamp).getTime() !== new Date(newIso).getTime();
      let nextGeoInfo = event.geoInfo;
      if (statusChanged) {
        nextGeoInfo = buildGeoInfoWithApprover(nextGeoInfo, authUser);
      }
      if (contentChanged) {
        nextGeoInfo = buildGeoInfoWithModifier(nextGeoInfo, authUser);
      }
      // El helper patchEventBodyForBackend en api.js se encarga del
      // workaround de approvalStatus=0 (mandarlo como "0" string).
      await updateEvent(event.id, {
        eventType: Number(eventType),
        timestamp: newIso,
        approvalStatus: Number(approvalStatus),
        geoInfo: nextGeoInfo,
      });
      toast.success(FEEDBACK.eventEdited({
        eventType,
        dayKey: dayKeyInZone(newIso, tz),
        time: formatHmInZone(newIso, tz),
        approvalStatus,
      }));
      onSaved?.({
        ...event,
        eventType: Number(eventType),
        timestamp: newIso,
        approvalStatus: Number(approvalStatus),
        geoInfo: nextGeoInfo,
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'No se pudo guardar el cambio.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold">Editar fichaje</div>
            <h2 className="font-display text-base font-bold text-ink inline-flex items-center gap-2">
              <ClockIcon size={15} className="text-primary" /> Modificar registro #{event.id}
            </h2>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors" aria-label="Cerrar">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3.5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">
              Tipo de fichaje
            </label>
            <select
              value={eventType}
              onChange={(e) => setEventType(Number(e.target.value))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm bg-white"
            >
              {EVENT_TYPE_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.items.map((t) => (
                    <option key={t} value={t}>{EVENT_TYPE_LABELS[t]}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">
              Fecha y hora
            </label>
            <input
              type="datetime-local"
              value={localTimestamp}
              onChange={(e) => setLocalTimestamp(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
            />
            <div className="mt-1 text-[11px] text-ink-muted">
              Indica la fecha y hora exactas en las que quieres que quede el fichaje.
              {tzHint && <> ({tzHint})</>}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1.5">
              Estado de aprobación
            </label>
            <select
              value={approvalStatus}
              onChange={(e) => setApprovalStatus(Number(e.target.value))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm bg-white"
            >
              {Object.entries(APPROVAL_STATUS_LABELS).map(([k, label]) => (
                <option key={k} value={Number(k)}>{label}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="text-[11px] text-ink-muted">
              <b className="text-ink">Importante:</b> los cambios quedan registrados en la auditoría.
            </div>
            <div className="flex gap-2 shrink-0">
              <button type="button" onClick={onClose} disabled={submitting} className="px-3 py-1.5 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors disabled:opacity-50">
                Cancelar
              </button>
              <button type="submit" disabled={submitting} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60">
                {submitting && <Loader2 size={13} className="animate-spin" />}
                Guardar
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventEditModal;
